/**
 * Calculate campaign progress percentage based on start and end dates
 * @param startDate - Campaign start date (YYYY-MM-DD)
 * @param endDate - Campaign end date (YYYY-MM-DD)
 * @returns Progress percentage (0-100)
 */
export const calculateCampaignProgress = (startDate: string, endDate: string): number => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Set all times to midnight for accurate day comparison
  now.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  // If campaign hasn't started yet
  if (now < start) {
    return 0;
  }

  // If campaign has ended
  if (now > end) {
    return 100;
  }

  // Calculate progress
  const totalDuration = end.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  
  const progress = (elapsed / totalDuration) * 100;
  
  // Return rounded percentage
  return Math.round(Math.min(Math.max(progress, 0), 100));
};

/**
 * Parse social media channels from JSON string
 * @param socialMediaStr - JSON string like "[\"FACEBOOK\"]"
 * @returns Array of social media platforms
 */
export const parseSocialMediaChannels = (socialMediaStr: string): string[] => {
  try {
    return JSON.parse(socialMediaStr);
  } catch {
    return [];
  }
};

/**
 * Get campaign status label
 * @param startDate - Campaign start date
 * @param endDate - Campaign end date
 * @returns Status string: 'Scheduled', 'Active', or 'Ended'
 */
export const getCampaignStatus = (startDate: string, endDate: string): string => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  now.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  if (now < start) return 'Scheduled';
  if (now > end) return 'Ended';
  return 'Active';
};
